import {
  TransitPassEmployee,
  AgencyStats,
  KpiStat,
  TimeframeOption,
} from './transitPassTypes'

// ─── KPIs (timeframe-aware) ──────────────────────────────────────────────────

export const mockKpisByTimeframe: Record<TimeframeOption, KpiStat[]> = {
  '7d': [
    {
      label: 'Total Enrolled',
      value: 1284,
      sub: 'across all agencies',
      trend: 1.1,
      trendLabel: '+14 vs last week',
      accent: 'green',
    },
    {
      label: 'Active Cards',
      value: 1097,
      sub: '85% activation rate',
      trend: 2.3,
      trendLabel: '+25 activated this week',
      accent: 'green',
    },
    {
      label: 'Pending Activation',
      value: 142,
      sub: 'within 10-day window',
      accent: 'blue',
    },
    {
      label: 'Exceptions',
      value: 45,
      sub: 'need attention',
      trend: -8.2,
      trendLabel: '-4 vs last week',
      accent: 'red',
    },
    {
      label: 'Monthly Spend',
      value: '$128,400',
      sub: '$100/employee avg',
      trend: 1.1,
      trendLabel: '+1.1% vs last week',
      accent: 'neutral',
    },
  ],
  '30d': [
    {
      label: 'Total Enrolled',
      value: 1284,
      sub: 'across all agencies',
      trend: 4.2,
      trendLabel: '+52 vs last month',
      accent: 'green',
    },
    {
      label: 'Active Cards',
      value: 1097,
      sub: '85% activation rate',
      trend: 5.8,
      trendLabel: '+60 activated this month',
      accent: 'green',
    },
    {
      label: 'Pending Activation',
      value: 142,
      sub: 'within 10-day window',
      accent: 'blue',
    },
    {
      label: 'Exceptions',
      value: 45,
      sub: 'need attention',
      trend: -18,
      trendLabel: '-10 vs last month',
      accent: 'red',
    },
    {
      label: 'Monthly Spend',
      value: '$128,400',
      sub: '$100/employee avg',
      trend: 4.2,
      trendLabel: '+$5.2k vs last month',
      accent: 'neutral',
    },
  ],
  '90d': [
    {
      label: 'Total Enrolled',
      value: 1284,
      sub: 'across all agencies',
      trend: 18.6,
      trendLabel: '+202 vs last quarter',
      accent: 'green',
    },
    {
      label: 'Active Cards',
      value: 1097,
      sub: '85% activation rate',
      trend: 22.4,
      trendLabel: '+200 activated this quarter',
      accent: 'green',
    },
    {
      label: 'Pending Activation',
      value: 142,
      sub: 'within 10-day window',
      accent: 'blue',
    },
    {
      label: 'Exceptions',
      value: 45,
      sub: 'need attention',
      trend: -41,
      trendLabel: '-31 vs last quarter',
      accent: 'red',
    },
    {
      label: 'Monthly Spend',
      value: '$128,400',
      sub: '$100/employee avg',
      trend: 18.6,
      trendLabel: '+$20k vs last quarter',
      accent: 'neutral',
    },
  ],
}

// ─── Agency breakdown ────────────────────────────────────────────────────────

export const mockAgencies: AgencyStats[] = [
  {
    id: 'mtc',
    name: 'BayPass (MTC)',
    region: 'San Francisco Bay Area',
    color: '#2563EB',
    logoUrl: 'https://www.clipperbaypass.com/wp-content/uploads/2023/02/Clipper-BayPass_Logo_color.svg',
    enrolled: 748,
    active: 641,
    pending: 83,
    monthlySpend: 74800,
    activationRate: 86,
    trendValue: 3.2,
    trendLabel: '+23 this month',
  },
  {
    id: 'orca',
    name: 'ORCA',
    region: 'Seattle / Bellevue',
    color: '#16A34A',
    logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/3/37/ORCA_card_logo_2.svg/250px-ORCA_card_logo_2.svg.png',
    enrolled: 312,
    active: 271,
    pending: 38,
    monthlySpend: 31200,
    activationRate: 87,
    trendValue: 6.1,
    trendLabel: '+18 this month',
  },
]

// ─── Pipeline counts ─────────────────────────────────────────────────────────

export const mockPipelineStages = [
  {
    key: 'submitted' as const,
    label: 'Submitted',
    count: 31,
    description: 'Card number received, awaiting processing',
    color: '#3B82F6',
  },
  {
    key: 'processing' as const,
    label: 'Processing',
    count: 48,
    description: 'Queued for portal upload',
    color: '#F59E0B',
  },
  {
    key: 'uploaded' as const,
    label: 'Uploaded',
    count: 63,
    description: 'Sent to Clipper portal, pending activation',
    color: '#8B5CF6',
  },
  {
    key: 'active' as const,
    label: 'Active',
    count: 1097,
    description: 'Card confirmed active on transit network',
    color: '#10B981',
  },
]

// ─── Employee records ─────────────────────────────────────────────────────────

export const mockEmployees: TransitPassEmployee[] = [
  // Active — BayPass
  { id: '1', name: 'Jordan Lee', initials: 'JL', site: 'SF HQ', cardNumberMasked: '•••• •••• 7821', cardFormat: '19-digit', portal: 'NextGen', submittedDate: 'Apr 2, 2026', daysElapsed: 33, status: 'active' },
  { id: '2', name: 'Priya Sharma', initials: 'PS', site: 'SF HQ', cardNumberMasked: '•••• 4493', cardFormat: '10-digit', portal: 'Classic', submittedDate: 'Apr 5, 2026', daysElapsed: 30, status: 'active' },
  { id: '3', name: 'Marcus Webb', initials: 'MW', site: 'Bellevue', cardNumberMasked: '•••• •••• 2204', cardFormat: '19-digit', portal: 'NextGen', submittedDate: 'Mar 28, 2026', daysElapsed: 38, status: 'active' },
  { id: '4', name: 'Aisha Patel', initials: 'AP', site: 'SF HQ', cardNumberMasked: '•••• 8812', cardFormat: '10-digit', portal: 'Classic', submittedDate: 'Apr 1, 2026', daysElapsed: 34, status: 'active' },
  { id: '5', name: 'Derek Nguyen', initials: 'DN', site: 'NYC', cardNumberMasked: '•••• •••• 5501', cardFormat: '19-digit', portal: 'NextGen', submittedDate: 'Apr 10, 2026', daysElapsed: 25, status: 'active' },
  // Pending / uploaded
  { id: '6', name: 'Sam Torres', initials: 'ST', site: 'SF HQ', cardNumberMasked: '•••• •••• 3390', cardFormat: '19-digit', portal: 'NextGen', submittedDate: 'Apr 26, 2026', daysElapsed: 9, status: 'uploaded' },
  { id: '7', name: 'Lily Chen', initials: 'LC', site: 'Bellevue', cardNumberMasked: '•••• 7703', cardFormat: '10-digit', portal: 'Classic', submittedDate: 'Apr 27, 2026', daysElapsed: 8, status: 'processing' },
  { id: '8', name: 'Omar Hassan', initials: 'OH', site: 'NYC', cardNumberMasked: '•••• •••• 9911', cardFormat: '19-digit', portal: 'NextGen', submittedDate: 'Apr 28, 2026', daysElapsed: 7, status: 'submitted' },
  { id: '9', name: 'Kenji Yamamoto', initials: 'KY', site: 'SF HQ', cardNumberMasked: '•••• •••• 1147', cardFormat: '19-digit', portal: 'NextGen', submittedDate: 'Apr 29, 2026', daysElapsed: 6, status: 'submitted' },
  { id: '10', name: 'Rachel Kim', initials: 'RK', site: 'SF HQ', cardNumberMasked: '•••• 2284', cardFormat: '10-digit', portal: 'Classic', submittedDate: 'Apr 25, 2026', daysElapsed: 10, status: 'uploaded' },
  // Exceptions
  { id: '11', name: 'Chris Park', initials: 'CP', site: 'SF HQ', cardNumberMasked: '•••• •••• 6628', cardFormat: '19-digit', portal: 'NextGen', submittedDate: 'Apr 20, 2026', daysElapsed: 15, status: 'exception', exceptionType: 'approaching_deadline' },
  { id: '12', name: 'Nina Osei', initials: 'NO', site: 'Bellevue', cardNumberMasked: '•••• 3371', cardFormat: '10-digit', portal: 'Classic', submittedDate: 'Apr 18, 2026', daysElapsed: 17, status: 'exception', exceptionType: 'transposed_digits' },
  { id: '13', name: 'Alex Rivera', initials: 'AR', site: 'NYC', cardNumberMasked: '•••• •••• 4480', cardFormat: '19-digit', portal: 'NextGen', submittedDate: 'Apr 15, 2026', daysElapsed: 20, status: 'exception', exceptionType: 'failed_upload' },
  { id: '14', name: 'Tamara Ross', initials: 'TR', site: 'SF HQ', cardNumberMasked: '•••• 5519', cardFormat: '10-digit', portal: 'Classic', submittedDate: 'Apr 22, 2026', daysElapsed: 13, status: 'exception', exceptionType: 'inactive_employee' },
]

// ─── Audit log ───────────────────────────────────────────────────────────────

export const mockAuditLog = [
  { id: '1', timestamp: 'May 4, 2026 11:00 PM', action: 'Auto-upload to NextGen portal', records: 14, status: 'success' },
  { id: '2', timestamp: 'May 3, 2026 11:00 PM', action: 'Auto-upload to Classic portal', records: 6, status: 'success' },
  { id: '3', timestamp: 'May 3, 2026 11:00 PM', action: 'Auto-upload to NextGen portal', records: 19, status: 'success' },
  { id: '4', timestamp: 'May 2, 2026 11:00 PM', action: 'Auto-upload to Classic portal', records: 3, status: 'manually_completed' },
  { id: '5', timestamp: 'May 1, 2026 11:00 PM', action: 'Auto-upload to NextGen portal', records: 22, status: 'success' },
]
